import React, {
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import {
    Alert,
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Tooltip,
    FormControl,
    FormControlLabel,
    FormLabel,
    LinearProgress,
    MenuItem,
    Radio,
    RadioGroup,
    Switch,
    TextField,
    Typography,
    Paper,
} from "@mui/material";

import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";

import { useAuth } from "../../components/TokenHandler";
import * as XLSX from "xlsx";
import {
    parsePdfPath,
    normalizeName,
    getFacilitySearchTerms,
    calculateCorporationNameScore,
    filterCandidatesToBestCorporation,
    findFacilityMatchWithDirectionFallback,
} from "./batchPdfFacilityMatching";
import {
    parseOptInSelection,
    validateConsentEffectiveDate,
    createPdfConsentFieldReviews,
    isConfirmedConsentFieldReview,
} from "./batchPdfConsentFieldReview";

const UPLOAD_TYPES = {
    INITIATION: "batch-initiation",
    PDF: "batch-pdf-upload",
    FACILITY_GENERAL: "facility-general-bulk-upload",
};

const TERMINAL_BATCH_STATUSES = new Set([
    "completed",
    "completed_with_errors",
    "completed_with_email_error",
    "failed",
    "cancelled",
]);


const BatchUploadModal = ({ open, onClose }) => {
    const {
        keycloak: kc,
        givenName,
        familyName,
        email,
        roles,
    } = useAuth();

    const canUseAllBatchUploads =
        roles.includes("admin_role") ||
        roles.includes("consent_role-admin");

    const canUseBatchInitiation =
        canUseAllBatchUploads ||
        roles.includes("consent_role-batch-initiation-uploader");

    const pollingTimerRef = useRef(null);
    const serverUploadPollingRef = useRef(null);
    const uploadGenerationRef = useRef(0);
    const facilityValidationGenerationRef = useRef(0);

    const [uploadType, setUploadType] = useState(
        UPLOAD_TYPES.INITIATION
    );

    const [file, setFile] = useState(null);
    const [error, setError] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [parsedRows, setParsedRows] = useState([]);
    const [pdfFacilityGroups, setPdfFacilityGroups] = useState([]);
    const [pdfPatientReviews, setPdfPatientReviews] = useState([]);
    const [pdfConsentFieldReviews, setPdfConsentFieldReviews] = useState([]);
    const [showAllPatientRows, setShowAllPatientRows] = useState(false);
    const [showAllConsentFields, setShowAllConsentFields] = useState(false);
    const [isValidatingFacilities, setIsValidatingFacilities] = useState(false);
    const [showAllFacilityMatches, setShowAllFacilityMatches] = useState(false);
    const [reportEmail, setReportEmail] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [hasSubmittedBatch, setHasSubmittedBatch] = useState(false);

    const [dryRun, setDryRun] = useState(true);
    const [validatePdf, setValidatePdf] = useState(true);
    const [batchId, setBatchId] = useState("");
    const [batchStatus, setBatchStatus] = useState(null);
    const [isPolling, setIsPolling] = useState(false);

    // Browser -> Node transfer progress (separate from background batch progress).
    const [spreadsheetUploadProgress, setSpreadsheetUploadProgress] = useState(null);
    const [spreadsheetUploadLoaded, setSpreadsheetUploadLoaded] = useState(0);
    const [spreadsheetUploadTotal, setSpreadsheetUploadTotal] = useState(0);
    const [spreadsheetUploadStage, setSpreadsheetUploadStage] = useState("idle");
    const [serverUploadProgress, setServerUploadProgress] = useState(0);

    const lastSyncedAuthRef = useRef({
        batchId: null,
        token: null,
    });

    const userEmail =
        email ||
        kc?.idTokenParsed?.email ||
        "";

    const isBatchInitiation =
        uploadType === UPLOAD_TYPES.INITIATION;

    const isBatchPdfUpload =
        uploadType === UPLOAD_TYPES.PDF;

    const isFacilityGeneralUpload =
        uploadType === UPLOAD_TYPES.FACILITY_GENERAL;

    const initiationExpectedColumns = [
        "Facility",
        "Provider Number",
        "Patient ID",
        "Patient Last Name",
        "Patient First Name",
        "POA first name",
        "POA last name",
        "POA email",
        "POA phone",
        "POA relationship",
    ];

    const facilityGeneralExpectedColumns = [
        "Corp Final",
        "pdf_path",
        "Opt-In Status",
        "facility/general",
    ];

    const batchPdfExpectedColumns = [
        "Corp Final",
        "TH Consent Found?",
        "Answer Notes",
        "pdf_path",
        "page_tapestry",
        "Status",
        "Date Completed",
        "Date Added to Master",
        "Opt-In Status",
        "effective date",
        "facility/general",
        "POA Yes/No",
        "poa first_name",
        "poa last_name",
        "poa email",
        "poa phone",
        "poa relationship",
    ];

    const getBatchPdfStatusCategory = (value) => {
        const status = String(value ?? "").trim().replace(/\s+/g, " ").toLowerCase();
        if (status === "th consent found") return "process";
        if (status === "no th consent") return "skip";
        return "review";
    };

    const batchPdfStatusCounts = useMemo(() => {
        return parsedRows.reduce((counts, row) => {
            counts[getBatchPdfStatusCategory(row.Status)] += 1;
            return counts;
        }, { process: 0, skip: 0, review: 0 });
    }, [parsedRows]);

    const pdfPatientReviewCounts = useMemo(() => {
        const excludedFacilities = new Set(pdfFacilityGroups.filter(group => group.excluded).map(group => group.key));
        return pdfPatientReviews.reduce((counts, row) => {
            if (row.excluded) counts.excluded += 1;
            else if (excludedFacilities.has(row.groupKey)) counts.excludedByFacility += 1;
            else if (row.confirmed) counts.confirmed += 1;
            else counts.unresolved += 1;
            return counts;
        }, { excluded: 0, excludedByFacility: 0, confirmed: 0, unresolved: 0 });
    }, [pdfPatientReviews, pdfFacilityGroups]);

    const activePdfPatientRows = useMemo(() => {
        const byGroup = new Map(pdfFacilityGroups.map(group => [group.key, group]));
        return pdfPatientReviews.filter(row => !row.excluded && !byGroup.get(row.groupKey)?.excluded);
    }, [pdfPatientReviews, pdfFacilityGroups]);

    const pdfFacilityValidationCounts = useMemo(() =>
        pdfFacilityGroups.reduce((counts, group) => {
            const activeCount = pdfPatientReviews.filter(row => row.groupKey === group.key && !row.excluded).length;
            if (group.excluded) {
                counts.excluded += 1;
                counts.excludedRows += activeCount;
            } else if (activeCount === 0) {
                // All rows in this group are individually excluded. No match is needed.
            } else if (group.status === "matched") {
                counts.matched += 1;
            } else {
                counts.unresolved += 1;
            }
            return counts;
        }, { matched: 0, unresolved: 0, excluded: 0, excludedRows: 0 }), [pdfFacilityGroups, pdfPatientReviews]);

    const pdfConsentFieldReviewCounts = useMemo(() => {
        const included = new Set(activePdfPatientRows.map(row => row.rowIndex));
        return pdfConsentFieldReviews.reduce((counts, row) => {
            if (!included.has(row.rowIndex)) counts.excluded += 1;
            else if (isConfirmedConsentFieldReview(row)) counts.confirmed += 1;
            else counts.unresolved += 1;
            return counts;
        }, { confirmed: 0, unresolved: 0, excluded: 0 });
    }, [pdfConsentFieldReviews, activePdfPatientRows]);

    const pdfIncludedProcessRows = activePdfPatientRows.length;

    const expectedColumns = isBatchPdfUpload
        ? batchPdfExpectedColumns
        : isFacilityGeneralUpload
            ? facilityGeneralExpectedColumns
            : initiationExpectedColumns;

    const stopPolling = () => {
        if (pollingTimerRef.current) {
            clearTimeout(pollingTimerRef.current);
            pollingTimerRef.current = null;
        }

        setIsPolling(false);
    };

    useEffect(() => {
        return () => {
            uploadGenerationRef.current += 1;
            if (pollingTimerRef.current) clearTimeout(pollingTimerRef.current);
            if (serverUploadPollingRef.current) clearTimeout(serverUploadPollingRef.current);
        };
    }, []);

    useEffect(() => {
        if (open && !reportEmail && userEmail) {
            setReportEmail(userEmail);
        }
    }, [open, reportEmail, userEmail]);

    const resetUploadState = () => {
        stopPolling();
        lastSyncedAuthRef.current = { batchId: null, token: null };
        uploadGenerationRef.current += 1;
        if (serverUploadPollingRef.current) clearTimeout(serverUploadPollingRef.current);
        serverUploadPollingRef.current = null;
        setServerUploadProgress(0);
        setFile(null);
        setError("");
        setSuccessMessage("");
        setParsedRows([]);
        setPdfFacilityGroups([]);
        setPdfPatientReviews([]);
        setPdfConsentFieldReviews([]);
        setShowAllPatientRows(false);
        setShowAllConsentFields(false);
        setIsValidatingFacilities(false);
        facilityValidationGenerationRef.current += 1;
        setShowAllFacilityMatches(false);
        setIsSubmitting(false);
        setHasSubmittedBatch(false);
        setBatchId("");
        setBatchStatus(null);
        setSpreadsheetUploadProgress(null);
        setSpreadsheetUploadLoaded(0);
        setSpreadsheetUploadTotal(0);
        setSpreadsheetUploadStage("idle");
    };

    const buildCreatedBy = (action, emailOverride) => ({
        date: new Date().toISOString(),
        email: String(emailOverride || userEmail || "").trim(),
        action,
        givenName:
            givenName ||
            kc?.idTokenParsed?.given_name ||
            "",
        familyName:
            familyName ||
            kc?.idTokenParsed?.family_name ||
            "",
    });

    const normalizeSpreadsheetRows = (rows) => {
        return rows.map((row) => {
            const normalizedRow = {};

            Object.entries(row).forEach(([columnName, value]) => {
                const normalizedColumnName = String(columnName).trim();

                let normalizedValue =
                    typeof value === "string" ? value.trim() : value;

                if (
                    normalizedColumnName === "POA relationship" &&
                    String(normalizedValue ?? "").trim() === ""
                ) {
                    normalizedValue = "Responsible party";
                }

                normalizedRow[normalizedColumnName] = normalizedValue;
            });

            return normalizedRow;
        });
    };

    const validateColumns = (rows) => {
        if (!rows || rows.length === 0) {
            return "The spreadsheet does not contain any rows.";
        }

        const actualColumns = Object.keys(rows[0]);
        const missingColumns = expectedColumns.filter(
            (column) => !actualColumns.includes(column)
        );

        if (missingColumns.length > 0) {
            return `Missing required columns: ${missingColumns.join(", ")}`;
        }

        return "";
    };

    const parseSpreadsheetToJson = async (selectedFile) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = (event) => {
                try {
                    const workbook = XLSX.read(event.target.result, {
                        type: "array",
                        cellDates: true,
                    });

                    const firstSheetName = workbook.SheetNames[0];

                    if (!firstSheetName) {
                        reject(new Error("No sheets were found in the spreadsheet."));
                        return;
                    }

                    const worksheet = workbook.Sheets[firstSheetName];

                    const jsonRows = XLSX.utils.sheet_to_json(worksheet, {
                        defval: "",
                        raw: false,
                    });

                    resolve(normalizeSpreadsheetRows(jsonRows));
                } catch (err) {
                    reject(err);
                }
            };

            reader.onerror = () => {
                reject(new Error("Unable to read spreadsheet file."));
            };

            reader.readAsArrayBuffer(selectedFile);
        });
    };

    const handleUploadTypeChange = (event) => {
        const newUploadType = event.target.value;

        if (
            !canUseAllBatchUploads &&
            newUploadType !== UPLOAD_TYPES.INITIATION
        ) {
            return;
        }

        resetUploadState();
        setUploadType(newUploadType);
    };

    const groupKeyForPdf = (parsed) =>
        `${normalizeName(parsed.corporationName)}::${normalizeName(parsed.facilityName)}`;

    const updatePdfFacilityGroup = (key, changes) => {
        setPdfFacilityGroups((current) => current.map((group) =>
            group.key === key ? { ...group, ...changes } : group));
    };

    const getSearchOrgs = async (q) => {
        const response = await fetch(
            `${process.env.REACT_APP_API_URL}/search_orgs4?q=${encodeURIComponent(q)}&all=true`,
            { headers: { Authorization: `Bearer ${kc.token}` } }
        );
        const data = await response.json().catch(() => null);
        if (!response.ok) throw new Error(data?.error || `Facility search failed (HTTP ${response.status}).`);
        if (!Array.isArray(data)) throw new Error("/search_orgs4 did not return an array.");
        return data;
    };

    const createPdfPatientReviews = (rows) => rows.flatMap((row, rowIndex) => {
        if (getBatchPdfStatusCategory(row.Status) !== "process") return [];
        const parsed = parsePdfPath(row.pdf_path, { allowInvalidPatientName: true });
        if (!parsed.valid) {
            throw new Error(`Invalid PDF path "${row.pdf_path}": ${parsed.error}`);
        }
        // Offer a convenience suggestion for "Edes Randy A.", but never approve
        // an unpunctuated name with a middle initial without user confirmation.
        const words = (parsed.rawPatientName || "").split(/\s+/).filter(Boolean);
        const lastToken = words[words.length - 1] || "";
        const canSuggest = Boolean(parsed.patientNameError) && words.length === 3 && /^[A-Za-z]\.?$/.test(lastToken);
        const groupKey = groupKeyForPdf(parsed);
        return [{
            rowIndex, groupKey, pdfPath: row.pdf_path,
            rawPatientName: parsed.rawPatientName || "",
            firstName: canSuggest ? `${words[1]} ${words[2]}` : (parsed.patientFirstName || ""),
            lastName: canSuggest ? words[0] : (parsed.patientLastName || ""),
            parseError: parsed.patientNameError || "",
            confirmed: !parsed.patientNameError,
            manuallyEdited: false,
            excluded: false,
        }];
    });

    const updatePdfConsentFieldReview = (rowIndex, changes) => {
        setPdfConsentFieldReviews(current => current.map(row =>
            row.rowIndex === rowIndex ? { ...row, ...changes } : row));
    };

    const updatePdfPatientReview = (rowIndex, changes) => {
        setPdfPatientReviews(current => current.map(row =>
            row.rowIndex === rowIndex ? { ...row, ...changes } : row));
    };

    const validPatientReviewName = (value) => {
        const name = String(value || "").trim();
        return name.length >= 1 && name.length <= 120 && !/[\r\n\x00-\x1f<>\/\\]/.test(name);
    };

    const createPdfFacilityGroups = (rows) => {
        const groups = new Map();
        for (const row of rows) {
            if (getBatchPdfStatusCategory(row.Status) !== "process") continue;
            const parsed = parsePdfPath(row.pdf_path, { allowInvalidPatientName: true });
            if (!parsed.valid) throw new Error(`Invalid PDF path "${row.pdf_path}": ${parsed.error}`);
            const key = groupKeyForPdf(parsed);
            if (groups.has(key)) {
                groups.get(key).count += 1;
            } else {
                groups.set(key, {
                    key, corporationName: parsed.corporationName,
                    facilityName: parsed.facilityName, examplePdfPath: row.pdf_path,
                    count: 1, status: "review", selected: null, excluded: false,
                    selectionMethod: "", score: 0, reason: "Awaiting search",
                    searchTerm: parsed.facilityName.replace(/^\(?\s*(East|West)\s*\)?\s*/i, "").trim(),
                    searchOptions: [], searching: false,
                });
            }
        }
        return [...groups.values()];
    };

    const isUsableSelectedFacility = (candidate) =>
        candidate && candidate.corp_unique_id != null &&
        candidate.facility_unique_id != null &&
        String(candidate.fac_id ?? "").trim() &&
        String(candidate.ouid ?? "").trim() &&
        String(candidate.source_id ?? "").trim();

    const validatePdfFacilityGroups = async (groups, generation) => {
        setIsValidatingFacilities(true);
        const corpRequests = new Map();
        const getCorporation = (name) => {
            const key = normalizeName(name);
            if (!corpRequests.has(key)) corpRequests.set(key, getSearchOrgs(name));
            return corpRequests.get(key);
        };
        // Sequential group processing avoids hammering the search endpoint.
        for (const group of groups) {
            if (facilityValidationGenerationRef.current !== generation) return;
            try {
                const corporationResults = await getCorporation(group.corporationName);
                const corp = filterCandidatesToBestCorporation({
                    corporationName: group.corporationName, searchResults: corporationResults,
                });
                let found = findFacilityMatchWithDirectionFallback({
                    corporationName: group.corporationName,
                    facilityName: group.facilityName,
                    searchResults: corp.candidates,
                });

                // A 50-row response may be truncated. Verify via facility searches
                // before accepting a fuzzy match from the partial list.
                const capped = corporationResults.length === 50;
                const exact = found.status === "matched" &&
                    ["exact", "exact_facility_variant"].includes(found.matchMethod);
                if (found.status !== "matched" || (capped && !exact)) {
                    for (const q of getFacilitySearchTerms(group.facilityName, group.corporationName)) {
                        const results = await getSearchOrgs(q);
                        const sameCorp = results.filter((candidate) =>
                            calculateCorporationNameScore(group.corporationName, candidate.corp_name) >= 0.60);
                        const attempted = findFacilityMatchWithDirectionFallback({
                            corporationName: group.corporationName,
                            facilityName: group.facilityName,
                            searchResults: sameCorp,
                        });
                        if (attempted.status === "matched" &&
                            ["exact", "exact_facility_variant"].includes(attempted.matchMethod)) {
                            found = attempted;
                            break;
                        }
                        if (!capped && found.status !== "matched" && attempted.status === "matched") {
                            found = attempted;
                        }
                    }
                }
                const approved = found.status === "matched" &&
                    isUsableSelectedFacility(found.match) &&
                    !(capped && !["exact", "exact_facility_variant"].includes(found.matchMethod));
                if (facilityValidationGenerationRef.current !== generation) return;
                updatePdfFacilityGroup(group.key, approved ? {
                    status: "matched", selected: found.match,
                    selectionMethod: `automatic:${found.matchMethod}`,
                    score: found.confidence,
                    reason: "Facility selected automatically",
                } : {
                    status: "review", selected: null,
                    reason: !isUsableSelectedFacility(found.match) && found.status === "matched"
                        ? "Match is missing an ID, OU ID, fac_id, or source_id. Select a valid facility."
                        : capped ? "Incomplete or ambiguous API results. Search and select the correct facility."
                            : (found.reason || "Search and select the correct facility."),
                });
            } catch (err) {
                if (facilityValidationGenerationRef.current !== generation) return;
                updatePdfFacilityGroup(group.key, {
                    status: "review", selected: null,
                    reason: `Automatic search failed: ${err.message}. Search manually.`,
                });
            }
        }
        if (facilityValidationGenerationRef.current === generation) setIsValidatingFacilities(false);
    };

    const handleManualFacilitySearch = async (group) => {
        const query = String(group.searchTerm || "").trim();
        if (query.length < 2) {
            updatePdfFacilityGroup(group.key, { reason: "Enter at least two characters." });
            return;
        }
        updatePdfFacilityGroup(group.key, { searching: true, searchOptions: [] });
        try {
            const results = await getSearchOrgs(query);
            const byIdentity = new Map();
            for (const result of results) {
                const key = `${result.corp_unique_id}|${result.facility_unique_id}|${result.fac_id}|${result.ouid}|${result.ehr_ds || ""}|${result.source_id}`;
                if (isUsableSelectedFacility(result) && !byIdentity.has(key)) byIdentity.set(key, result);
            }
            updatePdfFacilityGroup(group.key, {
                searching: false,
                searchOptions: [...byIdentity.values()],
                reason: byIdentity.size ? "Choose the correct corporation and facility below."
                    : `No eligible facilities returned for "${query}". Try another search.`,
            });
        } catch (error) {
            updatePdfFacilityGroup(group.key, { searching: false, reason: error.message });
        }
    };

    const facilityOptionLabel = (option) =>
        `${option.monday_facility_name || option.facility_name || "Unnamed"} | ` +
        `${option.corp_name || "Unknown corporation"} | Facility ID ${option.facility_unique_id}` +
        ` | ${option.source_system || ""} | ${option.fac_id || ""}`;

    // Store the identifiers inside the uploaded spreadsheet, not only in FormData.
    // The queued worker reads the CSV from MinIO in a separate process.
    // Excluded facility groups must be removed from the actual CSV before upload.
    const createApprovedPdfSpreadsheet = () => {
        const facilities = new Map(pdfFacilityGroups.map(group => [group.key, group]));
        const patients = new Map(pdfPatientReviews.map(row => [row.rowIndex, row]));
        const consentFields = new Map(pdfConsentFieldReviews.map(row => [row.rowIndex, row]));
        const included = [];
        for (let rowIndex = 0; rowIndex < parsedRows.length; rowIndex += 1) {
            const original = parsedRows[rowIndex];
            if (getBatchPdfStatusCategory(original.Status) !== "process") {
                included.push({ ...original });
                continue;
            }
            const patient = patients.get(rowIndex);
            if (!patient) throw new Error(`Missing patient review for spreadsheet row ${rowIndex + 2}.`);
            const group = facilities.get(patient.groupKey);
            if (patient.excluded || group?.excluded) continue;
            if (!patient.confirmed || !validPatientReviewName(patient.firstName) ||
                !validPatientReviewName(patient.lastName)) {
                throw new Error(`Confirm or exclude patient on spreadsheet row ${rowIndex + 2}.`);
            }
            if (!group || group.status !== "matched" || !isUsableSelectedFacility(group.selected)) {
                throw new Error(`Resolve the corporation and facility for spreadsheet row ${rowIndex + 2}.`);
            }
            const consentReview = consentFields.get(rowIndex);
            if (!isConfirmedConsentFieldReview(consentReview)) {
                throw new Error(`Correct or exclude Opt-In Status/effective date for spreadsheet row ${rowIndex + 2}.`);
            }
            const chosen = group.selected;
            const newRow = { ...original };
            // Replace the ORIGINAL spreadsheet field values. The existing worker
            // validates and reads these columns directly; no backend change needed.
            if (consentReview.optInEdited) {
                newRow["Opt-In Status"] = consentReview.optInStatus.trim();
            }
            if (consentReview.dateEdited) {
                newRow["effective date"] = consentReview.effectiveDate.trim();
            }
            // A prior results CSV may contain stale name corrections. Replace
            // them with the decisions made in THIS review session.
            delete newRow.batch_patient_first_name;
            delete newRow.batch_patient_last_name;
            delete newRow.batch_patient_review_status;
            delete newRow.batch_patient_name_source;
            Object.assign(newRow, {
                batch_selected_corp_unique_id: String(chosen.corp_unique_id),
                batch_selected_facility_unique_id: String(chosen.facility_unique_id),
                batch_selected_fac_id: String(chosen.fac_id),
                batch_selected_ouid: String(chosen.ouid),
                batch_selected_ehr_ds: String(chosen.ehr_ds || ""),
                batch_selected_source_id: String(chosen.source_id),
                batch_selected_corp_name: String(chosen.corp_name || ""),
                batch_selected_facility_name: String(chosen.monday_facility_name || chosen.facility_name || ""),
                batch_facility_selection_method: group.selectionMethod,
            });
            if (patient.manuallyEdited || patient.parseError) {
                Object.assign(newRow, {
                    batch_patient_first_name: patient.firstName.trim(),
                    batch_patient_last_name: patient.lastName.trim(),
                    batch_patient_review_status: "confirmed",
                    batch_patient_name_source: "user_confirmed",
                });
            }
            included.push(newRow);
        }
        if (!included.some(row => getBatchPdfStatusCategory(row.Status) === "process")) {
            throw new Error('No "TH Consent Found" rows remain after exclusions.');
        }
        const sheet = XLSX.utils.json_to_sheet(included);
        const csv = "\uFEFF" + XLSX.utils.sheet_to_csv(sheet);
        const filename = file.name.replace(/\.(csv|xlsx)$/i, "") + "-validated.csv";
        return new File([csv], filename, { type: "text/csv;charset=utf-8" });
    };

    const handleFileChange = async (event) => {
        const selectedFile = event.target.files?.[0];

        setError("");
        setSuccessMessage("");
        setParsedRows([]);
        setPdfFacilityGroups([]);
        setPdfPatientReviews([]);
        setPdfConsentFieldReviews([]);
        setShowAllPatientRows(false);
        setShowAllConsentFields(false);
        setIsValidatingFacilities(false);
        setShowAllFacilityMatches(false);
        const validationGeneration = ++facilityValidationGenerationRef.current;
        setHasSubmittedBatch(false);
        setBatchId("");
        setBatchStatus(null);
        setSpreadsheetUploadProgress(null);
        setSpreadsheetUploadLoaded(0);
        setSpreadsheetUploadTotal(0);
        setSpreadsheetUploadStage("idle");
        setServerUploadProgress(0);

        if (!selectedFile) {
            setFile(null);
            return;
        }

        if (!reportEmail && userEmail) {
            setReportEmail(userEmail);
        }

        const lowerFileName = selectedFile.name.toLowerCase();

        const isCsv =
            selectedFile.type === "text/csv" ||
            lowerFileName.endsWith(".csv");

        const isExcel =
            lowerFileName.endsWith(".xlsx") ||
            lowerFileName.endsWith(".xls");

        if (isBatchPdfUpload) {
            // The new backend accepts only CSV/XLSX, not XLS or ZIP files.
            if (!lowerFileName.endsWith(".csv") && !lowerFileName.endsWith(".xlsx")) {
                setFile(null);
                setError("Batch PDF Upload requires a CSV or XLSX spreadsheet, not a ZIP file.");
                return;
            }
        } else if (!isCsv && !isExcel) {
            setFile(null);
            setError("Please upload a CSV or Excel file.");
            return;
        }

        try {
            const rows = await parseSpreadsheetToJson(selectedFile);
            const columnError = validateColumns(rows);

            if (columnError) {
                setFile(null);
                setParsedRows([]);
                setError(columnError);
                return;
            }

            if (isBatchPdfUpload) {
                // Match the backend's exact-key path behavior. Require a corporation
                // folder and filename; no fallback search by PDF basename.
                const missingPathRow = rows.findIndex((row) =>
                    getBatchPdfStatusCategory(row.Status) === "process" &&
                    !String(row.pdf_path ?? "").trim()
                );
                if (missingPathRow >= 0) {
                    setFile(null);
                    setParsedRows([]);
                    setError(`Row ${missingPathRow + 2} has a blank pdf_path.`);
                    return;
                }
            }

            if (isBatchPdfUpload) {
                if (!kc?.token) throw new Error("Sign in to validate facilities.");
                const patientReviews = createPdfPatientReviews(rows);
                const consentFieldReviews = createPdfConsentFieldReviews(rows, getBatchPdfStatusCategory);
                const groups = createPdfFacilityGroups(rows);
                setPdfPatientReviews(patientReviews);
                setPdfConsentFieldReviews(consentFieldReviews);
                setPdfFacilityGroups(groups);
                setFile(selectedFile);
                setParsedRows(rows);
                await validatePdfFacilityGroups(groups, validationGeneration);
            } else {
                setFile(selectedFile);
                setParsedRows(rows);
            }
        } catch (err) {
            console.error("Spreadsheet parse error:", err);
            setFile(null);
            setParsedRows([]);
            setPdfFacilityGroups([]);
            setPdfPatientReviews([]);
            setPdfConsentFieldReviews([]);
            setIsValidatingFacilities(false);
            setError(err.message || "Unable to read spreadsheet. Please check the file format.");
        }
    };

    const handleClose = () => {
        resetUploadState();
        setReportEmail("");
        setDryRun(true);
        setValidatePdf(true);
        setUploadType(UPLOAD_TYPES.INITIATION);
        onClose();
    };

    const validateCommonSubmitFields = () => {
        if (!file) {
            return "Please choose a file before submitting.";
        }

        const emailToUse = String(reportEmail || userEmail || "").trim();

        if (!emailToUse) {
            return "Please enter a report email.";
        }

        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailToUse)) {
            return "Please enter a valid report email.";
        }

        if (!kc?.token) {
            return "Authentication token is missing. Please sign in again.";
        }

        if (
            (isBatchInitiation || isBatchPdfUpload || isFacilityGeneralUpload) &&
            parsedRows.length === 0
        ) {
            return "No spreadsheet rows were found.";
        }

        return "";
    };

    const submitBatchInitiation = async () => {
        const emailToUse = String(reportEmail || userEmail || "").trim();

        const payload = {
            created_by: buildCreatedBy("bulk upload", emailToUse),
            file_name: file.name,
            report_email: emailToUse,
            rows: parsedRows,
        };

        const response = await fetch(
            `${process.env.REACT_APP_API_URL}/consent/batch-upload/initiation`,
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${kc.token}`,
                },
                body: JSON.stringify(payload),
            }
        );

        const data = await response.json().catch(() => null);

        if (!response.ok) {
            throw new Error(
                data?.message || data?.error || "Batch initiation failed."
            );
        }

        setHasSubmittedBatch(true);
        setSuccessMessage(
            `Batch initiation submitted successfully. Batch ID: ${
                data?.batch_id || "created"
            }. Please wait for the emailed report before submitting another batch.`
        );
    };

    const syncBatchAuthorization = async (currentBatchId) => {
        const token = kc?.token;

        if (!token) {
            throw new Error("Your Keycloak session has expired.");
        }

        const previous = lastSyncedAuthRef.current;

        // Only send when the token or batch changes.
        if (
            previous.batchId === currentBatchId &&
            previous.token === token
        ) {
            return;
        }

        const response = await fetch(
            `${process.env.REACT_APP_API_URL}/consent/zippedpdfbulkupload/${currentBatchId}/auth-refresh`,
            {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        );

        if (!response.ok) {
            throw new Error("Unable to synchronize batch authorization.");
        }

        lastSyncedAuthRef.current = {
            batchId: currentBatchId,
            token,
        };
    };

    const pollBatchStatus = async (submittedBatchId) => {
        setIsPolling(true);

        const poll = async () => {
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/consent/zippedpdfbulkupload/${submittedBatchId}/status`,
                    {
                        headers: {
                            Authorization: `Bearer ${kc.token}`,
                        },
                    }
                );

                const data = await response.json().catch(() => null);

                if (!response.ok) {
                    const statusError = new Error(
                        data?.error || data?.message || "Unable to read batch status."
                    );
                    statusError.httpStatus = response.status;
                    throw statusError;
                }

                setBatchStatus(data);

                // Synchronize refreshed tokens WHILE the batch is running.
                if (!TERMINAL_BATCH_STATUSES.has(data.status)) {
                    try {
                        await syncBatchAuthorization(submittedBatchId);
                    } catch (syncError) {
                        console.warn(
                            "[Bulk Upload] Token synchronization failed:",
                            syncError.message
                        );

                        // Continue polling so a temporary synchronization
                        // failure does not hide the actual batch status.
                        // The next poll will retry synchronization.
                    }
                }

                if (TERMINAL_BATCH_STATUSES.has(data.status)) {
                    stopPolling();

                    if (data.status === "completed") {
                        setSuccessMessage("Batch PDF processing completed successfully. All source PDFs remain in MinIO; both reports are available to download.");
                    } else if (data.status === "completed_with_errors") {
                        setSuccessMessage(
                            "Batch PDF processing completed with errors. Download the result CSV for row details and the Excel review report for source-file eligibility. No PDFs were deleted."
                        );
                    } else if (data.status === "completed_with_email_error") {
                        setSuccessMessage(
                            "Batch PDF processing completed, but the result email could not be sent. Download the results CSV and deletion-review Excel file here. No source PDFs were deleted."
                        );
                    } else {
                        setError(
                            data.error_message ||
                            `Batch ended with status: ${data.status}`
                        );
                    }

                    return;
                }

                pollingTimerRef.current = setTimeout(poll, 1000);
            } catch (err) {
                if (err.httpStatus === 401 || err.httpStatus === 403) {
                    stopPolling();
                    setError("Your Keycloak session is no longer authorized to monitor this batch. Please sign in again.");
                    return;
                }

                // Temporary network/status failures must not permanently stop
                // the polling that delivers refreshed browser credentials.
                console.warn("[Bulk Upload] Status polling will retry:", err.message);
                pollingTimerRef.current = setTimeout(poll, 5000);
            }
        };

        await poll();
    };

    const pollFacilityGeneralBatchStatus = async (submittedBatchId) => {
        setIsPolling(true);

        const poll = async () => {
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/consent/facility-general-bulk-upload/${submittedBatchId}/status`,
                    {
                        headers: {
                            Authorization: `Bearer ${kc.token}`,
                        },
                    }
                );

                const data = await response.json().catch(() => null);

                if (!response.ok) {
                    throw new Error(
                        data?.error ||
                        data?.message ||
                        "Unable to read facility / general batch status."
                    );
                }

                setBatchStatus(data);

                if (TERMINAL_BATCH_STATUSES.has(data.status)) {
                    stopPolling();

                    if (data.status === "completed") {
                        setSuccessMessage(
                            "Facility / general batch completed successfully."
                        );
                    } else if (data.status === "completed_with_errors") {
                        setSuccessMessage(
                            "Facility / general batch completed with row errors. Download the result CSV for details."
                        );
                    } else if (data.status === "completed_with_email_error") {
                        setSuccessMessage(
                            "Facility / general batch completed, but the result email could not be sent."
                        );
                    } else {
                        setError(
                            data.error_message ||
                            `Batch ended with status: ${data.status}`
                        );
                    }

                    return;
                }

                pollingTimerRef.current = setTimeout(poll, 1000);
            } catch (err) {
                stopPolling();
                setError(
                    err.message ||
                    "Unable to monitor facility / general batch status."
                );
            }
        };

        await poll();
    };

    const submitFacilityGeneralUpload = async () => {
        const emailToUse = String(reportEmail || userEmail || "").trim();
        const formData = new FormData();

        formData.append("dryRun", String(dryRun));
        formData.append(
            "createdBy",
            JSON.stringify(
                buildCreatedBy("facility / general bulk upload", emailToUse)
            )
        );
        formData.append("spreadsheetFile", file);

        const response = await fetch(
            `${process.env.REACT_APP_API_URL}/consent/facility-general-bulk-upload`,
            {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${kc.token}`,
                },
                body: formData,
            }
        );

        const data = await response.json().catch(() => null);

        if (!response.ok) {
            throw new Error(
                data?.details ||
                data?.message ||
                data?.error ||
                "Facility / general batch upload failed."
            );
        }

        const submittedBatchId = data?.batchId || data?.batch_id;

        if (!submittedBatchId) {
            throw new Error(
                "The server accepted the upload but did not return a batch ID."
            );
        }

        setBatchId(submittedBatchId);
        setHasSubmittedBatch(true);
        setSuccessMessage(
            dryRun
                ? "Facility / general dry run submitted. Processing has started."
                : "Facility / general batch submitted. Processing has started."
        );

        await pollFacilityGeneralBatchStatus(submittedBatchId);
    };

    const submitBatchPdfUpload = async () => {
        const approvedFile = createApprovedPdfSpreadsheet();
        const emailToUse = String(reportEmail || userEmail || "").trim();
        const createdBy = buildCreatedBy("batch pdf upload", emailToUse);
        const baseUrl = `${process.env.REACT_APP_API_URL}/consent/zippedpdfbulkupload`;
        const generation = ++uploadGenerationRef.current;

        // Show progress interface immediately,
// before waiting for batch initialization.
        setSpreadsheetUploadStage("initializing");
        setSpreadsheetUploadProgress(0);
        setSpreadsheetUploadLoaded(0);
        setSpreadsheetUploadTotal(approvedFile.size);
        setServerUploadProgress(0);


        // Initialize batch before sending the spreadsheet.
// Refresh the Keycloak token before starting.
        if (!kc) {
            throw new Error("You must be signed in to upload.");
        }

        await kc.updateToken(60);

// Capture the token used to initialize this upload.
        const uploadToken = kc.token;

        if (!uploadToken) {
            throw new Error("Unable to obtain a valid authentication token.");
        }
        // Initialize batch before sending the spreadsheet. This lets the status
        // endpoint report server-side MinIO progress while XHR is awaiting a response.
        const initResponse = await fetch(`${baseUrl}/init`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${uploadToken}`,
            },
            body: JSON.stringify({
                filename: approvedFile.name,
                sizeBytes: approvedFile.size,
                dryRun,
                validatePdf,
                createdBy,
            }),
        });
        const initData = await initResponse.json().catch(() => null);
        if (!initResponse.ok || !initData?.batchId) {
            throw new Error(initData?.error || "Unable to initialize spreadsheet upload.");
        }
        const initializedBatchId = initData.batchId;
        setBatchId(initializedBatchId);
        setServerUploadProgress(0);

        // Poll independently of the pending XMLHttpRequest.
        const pollServerUpload = async () => {
            if (generation !== uploadGenerationRef.current) return;
            try {
                const response = await fetch(`${baseUrl}/${initializedBatchId}/status`, {
                    headers: { Authorization: `Bearer ${kc.token}` },
                });
                if (response.ok) {
                    const status = await response.json();
                    if (generation !== uploadGenerationRef.current) return;
                    if (status.status === "uploading") {
                        setServerUploadProgress(Number(status.minio_upload_percent || 0));
                    } else if (status.status === "queued" || status.status === "processing") {
                        setServerUploadProgress(100);
                    } else if (status.status === "failed") {
                        setError(status.error_message || "Spreadsheet storage failed.");
                        return;
                    }
                }
            } catch (err) {
                // A temporary polling failure should not interrupt the upload.
                console.warn("MinIO progress polling failed:", err);
            }
            if (generation === uploadGenerationRef.current) {
                serverUploadPollingRef.current = setTimeout(pollServerUpload, 1200);
            }
        };
        serverUploadPollingRef.current = setTimeout(pollServerUpload, 600);

        const formData = new FormData();
        formData.append("batchId", initializedBatchId);
        formData.append("dryRun", String(dryRun));
        formData.append("validatePdf", String(validatePdf));
        formData.append("createdBy", JSON.stringify(createdBy));
        formData.append("spreadsheetFile", approvedFile);

        setSpreadsheetUploadStage("transferring");
        setSpreadsheetUploadProgress(0);
        setSpreadsheetUploadLoaded(0);
        setSpreadsheetUploadTotal(approvedFile.size);

        let data;
        try {
            // XMLHttpRequest exposes upload progress; fetch() does not reliably
            // expose browser -> server upload progress.
            data = await new Promise((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                const url = `${process.env.REACT_APP_API_URL}/consent/zippedpdfbulkupload`;

                xhr.open("POST", url, true);
                xhr.setRequestHeader("Authorization", `Bearer ${uploadToken}`);
                // Do not set Content-Type: the browser supplies the multipart boundary.

                xhr.upload.onprogress = (event) => {
                    if (!event.lengthComputable || event.total <= 0) {
                        return;
                    }

                    const percent = Math.min(
                        100,
                        Math.round((event.loaded / event.total) * 100)
                    );
                    setSpreadsheetUploadLoaded(event.loaded);
                    setSpreadsheetUploadTotal(event.total);
                    setSpreadsheetUploadProgress(percent);
                    if (percent >= 100) {
                        setSpreadsheetUploadStage("server_finalizing");
                    }
                };

                xhr.upload.onload = () => {
                    // The request body reached the HTTP connection, but the backend
                    // may still be saving the spreadsheet to MinIO before responding.
                    setSpreadsheetUploadProgress(100);
                    setSpreadsheetUploadStage("server_finalizing");
                };

                xhr.onerror = () => reject(
                    new Error("Network error during spreadsheet upload. Check the connection and server logs.")
                );
                xhr.onabort = () => reject(new Error("Spreadsheet upload was cancelled."));
                xhr.ontimeout = () => reject(new Error("Spreadsheet upload timed out."));

                xhr.onload = () => {
                    let responseData = null;
                    try {
                        responseData = xhr.responseText
                            ? JSON.parse(xhr.responseText)
                            : null;
                    } catch {
                        // For a proxy failure, the response may not be JSON.
                    }

                    if (xhr.status < 200 || xhr.status >= 300) {
                        if (xhr.status === 413) {
                            reject(new Error(
                                "The spreadsheet exceeds the server's maximum upload size. " +
                                `Selected file: ${(approvedFile.size / (1024 * 1024)).toFixed(1)} MiB.`
                            ));
                            return;
                        }

                        reject(new Error(
                            responseData?.details ||
                            responseData?.message ||
                            responseData?.error ||
                            `Spreadsheet submission failed (HTTP ${xhr.status}).`
                        ));
                        return;
                    }

                    resolve(responseData);
                };

                xhr.send(formData);
            });

        } finally {
            if (serverUploadPollingRef.current) clearTimeout(serverUploadPollingRef.current);
            serverUploadPollingRef.current = null;
        }

        const submittedBatchId = data?.batchId || data?.batch_id;
        if (!submittedBatchId || submittedBatchId !== initializedBatchId) {
            throw new Error("Server did not confirm the initialized batch ID.");
        }
        setServerUploadProgress(100);
        setSpreadsheetUploadStage("complete");
        setSpreadsheetUploadProgress(100);
        setHasSubmittedBatch(true);
        setSuccessMessage(dryRun
            ? "Dry run submitted. Processing has started."
            : "Spreadsheet submitted. Source PDFs will be processed from MinIO and retained; a deletion-review Excel report will be generated.");
        await pollBatchStatus(submittedBatchId);
    };

    const handleSubmit = async () => {
        if (isSubmitting || hasSubmittedBatch) {
            return;
        }

        setError("");
        setSuccessMessage("");

        if (!canUseBatchInitiation) {
            setError("You do not have permission to use batch uploads.");
            return;
        }

        if (
            !canUseAllBatchUploads &&
            !isBatchInitiation
        ) {
            setError(
                "You only have permission to perform Batch Initiation uploads."
            );
            return;
        }

        const validationError = validateCommonSubmitFields();

        if (validationError) {
            setError(validationError);
            return;
        }

        if (isBatchPdfUpload && pdfIncludedProcessRows <= 0) {
            setError('No "TH Consent Found" rows remain after exclusions.');
            return;
        }
        if (isBatchPdfUpload && (isValidatingFacilities ||
            pdfFacilityGroups.some(group => !group.excluded && group.status !== "matched" &&
                pdfPatientReviews.some(row => row.groupKey === group.key && !row.excluded)))) {
            setError("Resolve or exclude every remaining PDF facility before submitting.");
            return;
        }

        if (isBatchPdfUpload && pdfPatientReviewCounts.unresolved > 0) {
            setError("Confirm or exclude every patient that needs review before submitting.");
            return;
        }
        if (isBatchPdfUpload && pdfConsentFieldReviewCounts.unresolved > 0) {
            setError("Correct or exclude every row with an invalid Opt-In Status or effective date before submitting.");
            return;
        }

        setIsSubmitting(true);

        try {
            if (isBatchInitiation) {
                await submitBatchInitiation();
            } else if (isBatchPdfUpload) {
                await submitBatchPdfUpload();
            } else {
                await submitFacilityGeneralUpload();
            }
        } catch (err) {
            console.error("Batch upload submit error:", err);
            if (isBatchPdfUpload) {
                setSpreadsheetUploadStage("failed");
            }
            setError(err.message || "Batch upload failed.");
        } finally {
            setIsSubmitting(false);
        }
    };

    // Both reports are generated in MinIO and downloaded through authenticated API routes.
    // Do not expose MinIO credentials or construct raw MinIO links in the browser.
    const downloadBatchReport = async (reportUrl, fallbackFilename, errorLabel) => {
        try {
            setError("");
            const response = await fetch(reportUrl, {
                headers: { Authorization: `Bearer ${kc.token}` },
            });

            if (!response.ok) {
                const data = await response.json().catch(() => null);
                throw new Error(
                    data?.error || data?.message || `Unable to download ${errorLabel}.`
                );
            }

            const blob = await response.blob();
            const disposition = response.headers.get("content-disposition") || "";
            const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
            const filename = filenameMatch?.[1] || fallbackFilename;
            const objectUrl = URL.createObjectURL(blob);
            const anchor = document.createElement("a");

            anchor.href = objectUrl;
            anchor.download = filename;
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            URL.revokeObjectURL(objectUrl);
        } catch (err) {
            setError(err.message || `Unable to download ${errorLabel}.`);
        }
    };

    const handleDownloadResult = async () => {
        if (!batchId) return;
        const resultUrl = isFacilityGeneralUpload
            ? `${process.env.REACT_APP_API_URL}/consent/facility-general-bulk-upload/${batchId}/result.csv`
            : `${process.env.REACT_APP_API_URL}/consent/zippedpdfbulkupload/${batchId}/result.csv`;

        await downloadBatchReport(
            resultUrl,
            "consent-bulk-upload-results.csv",
            "the results CSV"
        );
    };

    const handleDownloadDeletionReview = async () => {
        if (!batchId || !isBatchPdfUpload) return;

        await downloadBatchReport(
            `${process.env.REACT_APP_API_URL}/consent/zippedpdfbulkupload/${batchId}/deletion-candidates.xlsx`,
            "consent-bulk-upload-deletion-review.xlsx",
            "the deletion-review Excel report"
        );
    };

    const progressPercent = Number(batchStatus?.progress_percent ?? 0);

    const canDownloadResult =
        (isBatchPdfUpload || isFacilityGeneralUpload) &&
        batchStatus &&
        TERMINAL_BATCH_STATUSES.has(batchStatus.status) &&
        Boolean(batchStatus?.result_filename);

    const canDownloadDeletionReview = isBatchPdfUpload && canDownloadResult;

    const submitDisabled =
        !file ||
        (
            (isBatchInitiation || isBatchPdfUpload || isFacilityGeneralUpload) &&
            parsedRows.length === 0
        ) ||
        isSubmitting ||
        hasSubmittedBatch ||
        (isBatchPdfUpload && (isValidatingFacilities ||
            pdfFacilityValidationCounts.unresolved > 0 || pdfPatientReviewCounts.unresolved > 0 ||
            pdfConsentFieldReviewCounts.unresolved > 0 || pdfIncludedProcessRows <= 0));



    return (
        <Dialog
            open={open}
            onClose={isSubmitting ? undefined : handleClose}
            fullWidth
            maxWidth={isBatchPdfUpload ? "lg" : "sm"}
        >
            <DialogTitle>
                <Box display="flex" alignItems="center" gap={1}>
                    <CloudUploadIcon />
                    Batch Upload
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                <Box display="flex" flexDirection="column" gap={2}>
                    <FormControl>
                        <FormLabel>Upload type</FormLabel>

                        <RadioGroup
                            row
                            value={uploadType}
                            onChange={handleUploadTypeChange}
                        >
                            {canUseBatchInitiation && (
                                <FormControlLabel
                                    value={UPLOAD_TYPES.INITIATION}
                                    control={<Radio />}
                                    label="Batch Initiation"
                                    disabled={isSubmitting || isPolling}
                                />
                            )}

                            {canUseAllBatchUploads && (
                                <FormControlLabel
                                    value={UPLOAD_TYPES.PDF}
                                    control={<Radio />}
                                    label="Batch PDF Upload"
                                    disabled={isSubmitting || isPolling}
                                />
                            )}

                            {canUseAllBatchUploads && (
                                <FormControlLabel
                                    value={UPLOAD_TYPES.FACILITY_GENERAL}
                                    control={<Radio />}
                                    label="Facility / General"
                                    disabled={isSubmitting || isPolling}
                                />
                            )}
                        </RadioGroup>
                    </FormControl>

                    {isBatchInitiation && (
                        <>
                            <Typography variant="body2" color="text.secondary">
                                Upload a CSV, XLS, or XLSX file. The file will be staged,
                                processed, and a CSV report will be emailed after processing.
                            </Typography>

                            <Typography variant="body2" color="text.secondary">
                                Required columns: {initiationExpectedColumns.join(", ")}
                            </Typography>
                        </>
                    )}

                    {isBatchPdfUpload && (
                        <>
                            <Typography variant="body2" color="text.secondary">
                                Upload a CSV or XLSX spreadsheet. Consent PDFs are already stored
                                in MinIO and will be located by each row&apos;s pdf_path.
                                No ZIP or PDF upload is needed.
                            </Typography>

                            <Typography variant="body2" color="text.secondary">
                                Source: <strong>utils/sharepoint/</strong> + <code>pdf_path</code>
                                {" "}(e.g. <code>North American/patient.pdf</code>).
                                Folder paths must match MinIO exactly.
                            </Typography>

                            <Alert severity="info">
                                Only spreadsheet rows with <strong>Status: TH Consent Found</strong> will
                                be processed. Rows with <strong>No TH Consent</strong> will be skipped.
                                Unknown or blank Status values will be reported for review, not processed.
                            </Alert>

                            <Alert severity="info">
                                Source PDFs will <strong>not</strong> be deleted from MinIO.
                                After processing, an Excel deletion-review report will list
                                files eligible for later deletion and those requiring review.
                                File removal is a separate, manual process.
                            </Alert>

                            <Box>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={dryRun}
                                            onChange={(event) => setDryRun(event.target.checked)}
                                            disabled={isSubmitting || hasSubmittedBatch}
                                        />
                                    }
                                    label="Dry run"
                                />

                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                >
                                    Dry run validates matches and PDFs without creating
                                    consent records. All source PDFs are retained in both
                                    dry-run and normal processing modes.
                                </Typography>
                            </Box>

                            <Box>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={validatePdf}
                                            onChange={(event) =>
                                                setValidatePdf(event.target.checked)
                                            }
                                            disabled={isSubmitting || hasSubmittedBatch}
                                        />
                                    }
                                    label="Validate PDF files"
                                />
                            </Box>
                        </>
                    )}

                    {isFacilityGeneralUpload && (
                        <>
                            <Typography variant="body2" color="text.secondary">
                                Upload a CSV, XLS, or XLSX facility / general spreadsheet.
                                The batch will match the patient, locate the active Initiated
                                consent, generate the informed-consent PDF, and process the
                                record as facility / general.
                            </Typography>

                            <Typography variant="body2" color="text.secondary">
                                Required columns: {facilityGeneralExpectedColumns.join(", ")}
                            </Typography>

                            <Box>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={dryRun}
                                            onChange={(event) => setDryRun(event.target.checked)}
                                            disabled={isSubmitting || hasSubmittedBatch}
                                        />
                                    }
                                    label="Dry run"
                                />

                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                >
                                    Dry run performs matching and validation but does not
                                    update consent or store a generated patient PDF.
                                </Typography>
                            </Box>
                        </>
                    )}

                    {error && <Alert severity="error">{error}</Alert>}
                    {successMessage && (
                        <Alert severity="success">{successMessage}</Alert>
                    )}

                    {hasSubmittedBatch && isBatchInitiation && (
                        <Alert severity="info">
                            This batch initiation has been submitted. Please wait for the
                            emailed report before submitting another batch.
                        </Alert>
                    )}

                    <Button
                        variant="outlined"
                        component="label"
                        startIcon={<CloudUploadIcon />}
                        disabled={isSubmitting || hasSubmittedBatch}
                    >
                        Choose Spreadsheet

                        <input
                            type="file"
                            hidden
                            accept={isBatchPdfUpload ? ".csv,.xlsx" : ".csv,.xlsx,.xls"}
                            onChange={handleFileChange}
                        />
                    </Button>

                    {file && (
                        <Box>
                            <Typography variant="body2">
                                Selected file: <strong>{file.name}</strong>
                            </Typography>

                            {(isBatchInitiation || isBatchPdfUpload || isFacilityGeneralUpload) && (
                                <Typography variant="body2">
                                    Parsed rows: <strong>{parsedRows.length}</strong>
                                </Typography>
                            )}
                            {isBatchPdfUpload && (
                                <Typography variant="body2" color="text.secondary">
                                    To process: <strong>{pdfIncludedProcessRows}</strong>
                                    {" • "}Excluded facilities: <strong>{pdfFacilityValidationCounts.excludedRows}</strong>
                                    {" • "}Excluded patients: <strong>{pdfPatientReviewCounts.excluded}</strong>
                                    {" • "}Consent fields needing review: <strong>{pdfConsentFieldReviewCounts.unresolved}</strong>
                                    {" • "}No TH Consent (skip): <strong>{batchPdfStatusCounts.skip}</strong>
                                    {" • "}Status needs review: <strong>{batchPdfStatusCounts.review}</strong>
                                </Typography>
                            )}
                        </Box>
                    )}

                    {isBatchPdfUpload && file && (
                        <Box display="flex" flexDirection="column" gap={1.5}>
                            <Divider />
                            <Typography variant="subtitle1" fontWeight="bold">
                                Corporation and facility validation
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {pdfFacilityValidationCounts.matched} matched, {pdfFacilityValidationCounts.unresolved} need review,
                                {" "}{pdfFacilityValidationCounts.excluded} excluded
                                ({pdfFacilityValidationCounts.excludedRows} spreadsheet rows).
                                {" | "}{pdfFacilityGroups.length} distinct facility groups.
                                Only rows with Status "TH Consent Found" are checked.
                                Excluded facility rows are removed from the CSV sent to the backend.
                            </Typography>
                            {isValidatingFacilities && (
                                <Box>
                                    <LinearProgress />
                                    <Typography variant="caption">Checking facility names using /search_orgs4...</Typography>
                                </Box>
                            )}
                            {pdfFacilityValidationCounts.unresolved > 0 && !isValidatingFacilities && (
                                <Alert severity="warning">
                                    Resolve or exclude every unmatched or ambiguous facility below before processing.
                                </Alert>
                            )}
                            {pdfIncludedProcessRows <= 0 && !isValidatingFacilities && (
                                <Alert severity="warning">
                                    No processable PDF rows remain. Restore at least one excluded patient or facility before submitting.
                                </Alert>
                            )}
                            {pdfFacilityValidationCounts.unresolved === 0 && pdfIncludedProcessRows > 0 &&
                                !isValidatingFacilities && pdfFacilityGroups.length > 0 && (
                                    <Alert severity="success">
                                        All included facility groups are selected. Excluded rows will not be transmitted.
                                        The backend will verify the remaining facility IDs before inserting consents.
                                    </Alert>
                                )}
                            <FormControlLabel
                                control={<Switch checked={showAllFacilityMatches}
                                                 onChange={(event) => setShowAllFacilityMatches(event.target.checked)} />}
                                label="Show automatically matched facilities"
                            />
                            <Box display="flex" flexDirection="column" gap={1.5}
                                 sx={{ maxHeight: 440, overflowY: "auto" }}>
                                {pdfFacilityGroups.filter(group =>
                                    pdfPatientReviews.some(row => row.groupKey === group.key && !row.excluded) &&
                                    (showAllFacilityMatches || group.excluded || group.status !== "matched" || group.selectionMethod === "manual")
                                ).map(group => (
                                    <Paper key={group.key} variant="outlined" sx={{ p: 1.5 }}>
                                        <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1}>
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography variant="body2" fontWeight="bold">
                                                    {group.facilityName}
                                                </Typography>
                                                <Typography variant="caption" display="block" color="text.secondary">
                                                    Folder: {group.corporationName} | {group.count} spreadsheet row(s)
                                                </Typography>
                                            </Box>
                                            {!group.excluded && (
                                                <Tooltip title={`Exclude ${group.count} row(s) from this upload`}>
                                                    <span>
                                                        <IconButton size="small" color="error"
                                                                    aria-label={`Exclude ${group.facilityName} from upload`}
                                                                    disabled={isSubmitting || hasSubmittedBatch}
                                                                    onClick={() => updatePdfFacilityGroup(group.key, { excluded: true })}>
                                                            <CloseIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            )}
                                        </Box>
                                        {group.excluded ? (
                                            <Box display="flex" alignItems="center" gap={1} mt={1}>
                                                <Alert severity="info" sx={{ flex: 1, py: 0 }}>
                                                    Excluded. All {group.count} row(s) for this facility will be omitted from the uploaded CSV.
                                                </Alert>
                                                <Button size="small" variant="outlined"
                                                        disabled={isSubmitting || hasSubmittedBatch}
                                                        onClick={() => updatePdfFacilityGroup(group.key, { excluded: false })}>
                                                    Restore
                                                </Button>
                                            </Box>
                                        ) : group.status === "matched" && group.selected ? (
                                            <Box display="flex" alignItems="center" gap={1} mt={1}>
                                                <Typography variant="body2" sx={{ flex: 1 }}>
                                                    Selected: {facilityOptionLabel(group.selected)}
                                                    {" | "}{group.selectionMethod}
                                                </Typography>
                                                <Button size="small" onClick={() => updatePdfFacilityGroup(group.key, {
                                                    status: "review", selected: null, selectionMethod: "",
                                                    reason: "Search to change the selection.",
                                                })} disabled={isSubmitting}>Change</Button>
                                            </Box>
                                        ) : (
                                            <Box display="flex" flexDirection="column" gap={1} mt={1}>
                                                <Alert severity="warning" sx={{ py: 0 }}>
                                                    {group.reason}
                                                </Alert>
                                                <Box display="flex" gap={1}>
                                                    <TextField size="small" fullWidth label="Search corporation or facility"
                                                               value={group.searchTerm}
                                                               onChange={event => updatePdfFacilityGroup(group.key, { searchTerm: event.target.value })}
                                                               onKeyDown={event => { if (event.key === "Enter") {
                                                                   event.preventDefault(); handleManualFacilitySearch(group);
                                                               } }}
                                                    />
                                                    <Button variant="outlined" onClick={() => handleManualFacilitySearch(group)}
                                                            disabled={group.searching || isSubmitting}>
                                                        {group.searching ? <CircularProgress size={18} /> : "Search"}
                                                    </Button>
                                                </Box>
                                                {!!group.searchOptions.length && (
                                                    <Autocomplete
                                                        options={group.searchOptions}
                                                        getOptionLabel={facilityOptionLabel}
                                                        isOptionEqualToValue={(a, b) => a.facility_unique_id === b.facility_unique_id &&
                                                            a.corp_unique_id === b.corp_unique_id && a.fac_id === b.fac_id && a.ouid === b.ouid}
                                                        renderInput={params => <TextField {...params} label="Select verified facility" size="small" />}
                                                        onChange={(_, selected) => {
                                                            if (!selected) return;
                                                            updatePdfFacilityGroup(group.key, {
                                                                status: "matched", selected,
                                                                selectionMethod: "manual", score: 1,
                                                                reason: "Selected by user",
                                                            });
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                        )}
                                    </Paper>
                                ))}
                            </Box>
                        </Box>
                    )}

                    {isBatchPdfUpload && file && (
                        <Box display="flex" flexDirection="column" gap={1.5}>
                            <Divider />
                            <Typography variant="subtitle1" fontWeight="bold">Patient name review</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {pdfPatientReviewCounts.confirmed} confirmed, {pdfPatientReviewCounts.unresolved} need review,
                                {" "}{pdfPatientReviewCounts.excluded} individually excluded.
                                Corrections apply only to the patient lookup. The PDF path and facility stay unchanged.
                            </Typography>
                            {pdfPatientReviewCounts.unresolved > 0 && (
                                <Alert severity="warning">
                                    Review the patient names below, then Confirm or Exclude each row. Unresolved patients block submission.
                                </Alert>
                            )}
                            <FormControlLabel
                                control={<Switch checked={showAllPatientRows}
                                                 onChange={event => setShowAllPatientRows(event.target.checked)}
                                                 disabled={isSubmitting || hasSubmittedBatch} />}
                                label="Show all patient rows, including automatically parsed names"
                            />
                            <Box display="flex" flexDirection="column" gap={1.5}
                                 sx={{ maxHeight: 450, overflowY: "auto" }}>
                                {pdfPatientReviews.filter(row => showAllPatientRows || row.excluded || !row.confirmed || row.manuallyEdited)
                                    .map(row => {
                                        const facilityExcluded = pdfFacilityGroups.find(g => g.key === row.groupKey)?.excluded;
                                        return (
                                            <Paper key={row.rowIndex} variant="outlined" sx={{ p: 1.5 }}>
                                                <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                                                    <Box sx={{ minWidth: 0 }}>
                                                        <Typography variant="body2" fontWeight="bold">
                                                            Spreadsheet row {row.rowIndex + 2}: {row.rawPatientName || "Unrecognized patient name"}
                                                        </Typography>
                                                        <Typography variant="caption" display="block" color="text.secondary"
                                                                    sx={{ overflowWrap: "anywhere" }}>{row.pdfPath}</Typography>
                                                    </Box>
                                                    {!row.excluded && (
                                                        <Tooltip title="Exclude only this PDF row from the upload">
                                                            <span><IconButton size="small" color="error"
                                                                              aria-label={`Exclude patient on row ${row.rowIndex + 2}`}
                                                                              disabled={isSubmitting || hasSubmittedBatch}
                                                                              onClick={() => updatePdfPatientReview(row.rowIndex, { excluded: true })}>
                                                                <CloseIcon fontSize="small" />
                                                            </IconButton></span>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                                {row.excluded ? (
                                                    <Box display="flex" gap={1} alignItems="center" mt={1}>
                                                        <Alert severity="info" sx={{ flex: 1, py: 0 }}>
                                                            Excluded. This PDF row will not be sent to the backend.
                                                        </Alert>
                                                        <Button size="small" variant="outlined"
                                                                disabled={isSubmitting || hasSubmittedBatch}
                                                                onClick={() => updatePdfPatientReview(row.rowIndex, { excluded: false })}>
                                                            Restore
                                                        </Button>
                                                    </Box>
                                                ) : (
                                                    <Box display="flex" flexDirection="column" gap={1} mt={1}>
                                                        {!!row.parseError && !row.confirmed && (
                                                            <Alert severity="warning" sx={{ py: 0 }}>{row.parseError}</Alert>
                                                        )}
                                                        {facilityExcluded && (
                                                            <Alert severity="info" sx={{ py: 0 }}>
                                                                This entire facility is excluded, so this patient will not be uploaded.
                                                            </Alert>
                                                        )}
                                                        <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
                                                            <TextField size="small" label="Last name" value={row.lastName}
                                                                       sx={{ flex: 1, minWidth: 160 }}
                                                                       disabled={isSubmitting || hasSubmittedBatch}
                                                                       onChange={event => updatePdfPatientReview(row.rowIndex, {
                                                                           lastName: event.target.value, confirmed: false, manuallyEdited: true,
                                                                       })} />
                                                            <TextField size="small" label="First name (middle initial allowed)"
                                                                       value={row.firstName} sx={{ flex: 1, minWidth: 200 }}
                                                                       disabled={isSubmitting || hasSubmittedBatch}
                                                                       onChange={event => updatePdfPatientReview(row.rowIndex, {
                                                                           firstName: event.target.value, confirmed: false, manuallyEdited: true,
                                                                       })} />
                                                            {row.confirmed ? (
                                                                <Typography variant="caption" color="success.main">Confirmed</Typography>
                                                            ) : (
                                                                <Button size="small" variant="contained"
                                                                        disabled={isSubmitting || hasSubmittedBatch ||
                                                                            !validPatientReviewName(row.firstName) || !validPatientReviewName(row.lastName)}
                                                                        onClick={() => updatePdfPatientReview(row.rowIndex, {
                                                                            confirmed: true, manuallyEdited: true,
                                                                        })}>Confirm</Button>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                )}
                                            </Paper>
                                        );
                                    })}
                            </Box>
                        </Box>
                    )}

                    {isBatchPdfUpload && file && (
                        <Box display="flex" flexDirection="column" gap={1.5}>
                            <Divider />
                            <Typography variant="subtitle1" fontWeight="bold">
                                Consent details review
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {pdfConsentFieldReviewCounts.confirmed} valid or confirmed,
                                {" "}{pdfConsentFieldReviewCounts.unresolved} need correction,
                                {" "}{pdfConsentFieldReviewCounts.excluded} excluded by patient or facility.
                                Review happens before uploading the spreadsheet to the backend.
                            </Typography>
                            {pdfConsentFieldReviewCounts.unresolved > 0 && (
                                <Alert severity="warning">
                                    Correct the Opt-In Status and effective date for each flagged row,
                                    then click Confirm. Alternatively, click X to exclude that individual PDF row.
                                </Alert>
                            )}
                            <FormControlLabel
                                control={<Switch checked={showAllConsentFields}
                                                 onChange={event => setShowAllConsentFields(event.target.checked)}
                                                 disabled={isSubmitting || hasSubmittedBatch} />}
                                label="Show all consent detail rows, including valid entries"
                            />
                            <Box display="flex" flexDirection="column" gap={1.5}
                                 sx={{ maxHeight: 520, overflowY: "auto" }}>
                                {pdfConsentFieldReviews.filter(row => {
                                    const patient = pdfPatientReviews.find(p => p.rowIndex === row.rowIndex);
                                    const facilityExcluded = pdfFacilityGroups.some(g => g.key === patient?.groupKey && g.excluded);
                                    return showAllConsentFields || patient?.excluded ||
                                        (!facilityExcluded && (!isConfirmedConsentFieldReview(row) ||
                                            row.optInEdited || row.dateEdited));
                                }).map(row => {
                                    const patient = pdfPatientReviews.find(p => p.rowIndex === row.rowIndex);
                                    const group = pdfFacilityGroups.find(g => g.key === patient?.groupKey);
                                    const excluded = Boolean(patient?.excluded || group?.excluded);
                                    const optError = parseOptInSelection(row.optInStatus).error;
                                    const dateError = validateConsentEffectiveDate(row.effectiveDate).error;
                                    return (
                                        <Paper key={row.rowIndex} variant="outlined" sx={{ p: 1.5 }}>
                                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography variant="body2" fontWeight="bold">
                                                        Spreadsheet row {row.rowIndex + 2} | Patient: {
                                                        patient?.firstName && patient?.lastName
                                                            ? `${patient.firstName} ${patient.lastName}`
                                                            : patient?.rawPatientName || "Unrecognized patient"
                                                    }
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Facility: {group?.selected?.monday_facility_name ||
                                                        group?.selected?.facility_name || group?.facilityName || "Unrecognized facility"}
                                                        {" | "}Corporation: {group?.selected?.corp_name || group?.corporationName || "Unknown"}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" display="block"
                                                                sx={{ overflowWrap: "anywhere" }}>{row.pdfPath}</Typography>
                                                </Box>
                                                {!excluded && (
                                                    <Tooltip title="Exclude this individual PDF row from the uploaded CSV">
                                                        <span><IconButton size="small" color="error"
                                                                          aria-label={`Exclude consent details on row ${row.rowIndex + 2}`}
                                                                          disabled={isSubmitting || hasSubmittedBatch}
                                                                          onClick={() => updatePdfPatientReview(row.rowIndex, { excluded: true })}>
                                                            <CloseIcon fontSize="small" />
                                                        </IconButton></span>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                            {excluded ? (
                                                <Box display="flex" gap={1} alignItems="center" mt={1}>
                                                    <Alert severity="info" sx={{ flex: 1, py: 0 }}>
                                                        Excluded. This PDF row will not be sent to the backend.
                                                    </Alert>
                                                    {patient?.excluded && (
                                                        <Button size="small" variant="outlined"
                                                                disabled={isSubmitting || hasSubmittedBatch}
                                                                onClick={() => updatePdfPatientReview(row.rowIndex, { excluded: false })}>
                                                            Restore
                                                        </Button>
                                                    )}
                                                </Box>
                                            ) : (
                                                <Box display="flex" flexDirection="column" gap={1} mt={1}>
                                                    {(!row.confirmed || optError || dateError) && (
                                                        <Typography variant="caption" color="warning.main">
                                                            Original Opt-In Status: {row.originalOptInStatus || "(blank)"}
                                                            {" | "}Original effective date: {row.originalEffectiveDate || "(blank)"}
                                                        </Typography>
                                                    )}
                                                    <Box display="flex" alignItems="flex-start" flexWrap="wrap" gap={1}>
                                                        <TextField select size="small" label="Opt-In Status"
                                                                   value={row.optInStatus} sx={{ flex: 1, minWidth: 170 }}
                                                                   error={Boolean(optError)}
                                                                   helperText={optError || "OptIn = consent granted; OptOut = declined"}
                                                                   disabled={isSubmitting || hasSubmittedBatch}
                                                                   onChange={event => updatePdfConsentFieldReview(row.rowIndex, {
                                                                       optInStatus: event.target.value,
                                                                       optInEdited: true,
                                                                       confirmed: false,
                                                                   })}>
                                                            <MenuItem value=""><em>Select a status</em></MenuItem>
                                                            <MenuItem value="OptIn">OptIn</MenuItem>
                                                            <MenuItem value="OptOut">OptOut</MenuItem>
                                                        </TextField>
                                                        <TextField size="small" label="Effective date"
                                                                   placeholder="M/D/YYYY or YYYY-MM-DD"
                                                                   value={row.effectiveDate} sx={{ flex: 1, minWidth: 215 }}
                                                                   error={Boolean(dateError)}
                                                                   helperText={dateError || "Accepted: M/D/YYYY, M/D/YY, YYYY-MM-DD"}
                                                                   disabled={isSubmitting || hasSubmittedBatch}
                                                                   onChange={event => updatePdfConsentFieldReview(row.rowIndex, {
                                                                       effectiveDate: event.target.value,
                                                                       dateEdited: true,
                                                                       confirmed: false,
                                                                   })} />
                                                        {isConfirmedConsentFieldReview(row) ? (
                                                            <Typography variant="caption" color="success.main" sx={{ mt: 1 }}>
                                                                Validated
                                                            </Typography>
                                                        ) : (
                                                            <Button variant="contained" size="small" sx={{ mt: 0.5 }}
                                                                    disabled={isSubmitting || hasSubmittedBatch || Boolean(optError || dateError)}
                                                                    onClick={() => updatePdfConsentFieldReview(row.rowIndex, { confirmed: true })}>
                                                                Confirm
                                                            </Button>
                                                        )}
                                                    </Box>
                                                </Box>
                                            )}
                                        </Paper>
                                    );
                                })}
                            </Box>
                        </Box>
                    )}

                    <TextField
                        label="Report email"
                        value={reportEmail}
                        onChange={(event) => setReportEmail(event.target.value)}
                        fullWidth
                        size="small"
                        disabled={isSubmitting || hasSubmittedBatch}
                        helperText={
                            isBatchInitiation
                                ? "The processing report CSV will be emailed here."
                                : isBatchPdfUpload
                                    ? dryRun
                                        ? "Dry runs are not emailed. Both reports will be available for download after processing."
                                        : "The results CSV and deletion-review Excel report will be sent here and to the fixed backend email recipient."
                                    : dryRun
                                        ? "Dry runs are not emailed, but this email is included in createdBy."
                                        : "The completed result CSV will be emailed here."
                        }
                    />

                    {isBatchPdfUpload && spreadsheetUploadProgress !== null && (
                        <Box display="flex" flexDirection="column" gap={1}>
                            <Typography variant="body2" fontWeight="bold">
                                {spreadsheetUploadStage === "initializing"
                                    ? "Initializing batch..."
                                    : spreadsheetUploadStage === "transferring"
                                        ? "Uploading spreadsheet to server"
                                        : spreadsheetUploadStage === "server_finalizing"
                                            ? "Server is saving spreadsheet to MinIO..."
                                            : spreadsheetUploadStage === "complete"
                                                ? "Spreadsheet accepted"
                                                : spreadsheetUploadStage === "failed"
                                                    ? "Spreadsheet upload failed"
                                                    : "Spreadsheet transfer"}
                            </Typography>
                            <LinearProgress
                                variant={
                                    spreadsheetUploadStage === "initializing"
                                        ? "indeterminate"
                                        : "determinate"
                                }
                                value={
                                    spreadsheetUploadStage === "initializing"
                                        ? undefined
                                        : spreadsheetUploadProgress
                                }
                            />
                            <Typography variant="caption" color="text.secondary">
                                {spreadsheetUploadStage === "initializing"
                                    ? "Preparing upload..."
                                    : `${spreadsheetUploadProgress}% transferred`}
                                {spreadsheetUploadTotal > 0
                                    ? ` • ${(spreadsheetUploadLoaded / (1024 * 1024)).toFixed(1)} / ${(spreadsheetUploadTotal / (1024 * 1024)).toFixed(1)} MiB`
                                    : ""}
                            </Typography>
                            {spreadsheetUploadStage === "server_finalizing" && (
                                <Typography variant="caption" color="text.secondary">
                                    Browser transfer is complete. The MinIO progress bar below
                                    shows the server-side transfer.
                                </Typography>
                            )}
                        </Box>
                    )}

                    {isBatchPdfUpload && batchId && spreadsheetUploadStage !== "complete" && (
                        <Box display="flex" flexDirection="column" gap={1}>
                            <Typography variant="body2" fontWeight="bold">
                                Saving spreadsheet to MinIO: {serverUploadProgress}%
                            </Typography>
                            <LinearProgress
                                variant="determinate"
                                value={Math.min(100, Math.max(0, serverUploadProgress))}
                            />
                            <Typography variant="caption" color="text.secondary">
                                Server transfer to MinIO. Progress is reported by the backend.
                            </Typography>
                        </Box>
                    )}

                    {(isBatchPdfUpload || isFacilityGeneralUpload) &&
                        (batchId || batchStatus) &&
                        (!isBatchPdfUpload || spreadsheetUploadStage === "complete") && (
                            <Box display="flex" flexDirection="column" gap={1}>
                                {isBatchPdfUpload &&
                                    !TERMINAL_BATCH_STATUSES.has(batchStatus?.status) && (
                                        <Alert severity="warning">
                                            Keep this upload window open and remain signed in while PDFs
                                            are being processed. The worker receives refreshed Keycloak
                                            access tokens through this window; closing it stops updates.
                                        </Alert>
                                    )}
                                <Typography variant="body2">
                                    <strong>Batch ID:</strong> {batchId}
                                </Typography>

                                <Typography variant="body2">
                                    <strong>Status:</strong> {batchStatus?.status || "queued"}
                                </Typography>

                                <Typography variant="body2">
                                    <strong>Stage:</strong> {batchStatus?.stage || "queued"}
                                </Typography>

                                {isBatchPdfUpload && batchStatus?.stage === "deletion_manifest" && (
                                    <Typography variant="caption" color="text.secondary">
                                        Preparing the Excel deletion-review report. No source files are deleted.
                                    </Typography>
                                )}

                                <LinearProgress
                                    variant="determinate"
                                    value={Math.min(Math.max(progressPercent, 0), 100)}
                                />

                                <Typography variant="caption" color="text.secondary">
                                    {progressPercent}% complete
                                    {batchStatus?.total_rows
                                        ? ` • ${batchStatus.processed_rows || 0} of ${batchStatus.total_rows} rows processed`
                                        : ""}
                                    {batchStatus?.successful_rows !== undefined
                                        ? ` • ${batchStatus.successful_rows} successful`
                                        : ""}
                                    {batchStatus?.failed_rows !== undefined
                                        ? ` • ${batchStatus.failed_rows} failed`
                                        : ""}
                                    {isBatchPdfUpload && Number(batchStatus?.processed_rows || 0) -
                                    Number(batchStatus?.successful_rows || 0) -
                                    Number(batchStatus?.failed_rows || 0) > 0
                                        ? ` • ${Number(batchStatus.processed_rows || 0) -
                                        Number(batchStatus.successful_rows || 0) -
                                        Number(batchStatus.failed_rows || 0)} skipped`
                                        : ""}
                                </Typography>

                                {canDownloadResult && (
                                    <Button
                                        variant="outlined"
                                        startIcon={<DownloadIcon />}
                                        onClick={handleDownloadResult}
                                    >
                                        Download Result CSV
                                    </Button>
                                )}

                                {canDownloadDeletionReview && (
                                    <Button
                                        variant="outlined"
                                        startIcon={<DownloadIcon />}
                                        onClick={handleDownloadDeletionReview}
                                    >
                                        Download Deletion-Review Excel
                                    </Button>
                                )}
                            </Box>
                        )}

                    <Box>
                        <Typography variant="body2">
                            <strong>Uploaded By:</strong>{" "}
                            {givenName || kc?.idTokenParsed?.given_name || ""}{" "}
                            {familyName || kc?.idTokenParsed?.family_name || ""}
                        </Typography>

                        <Typography variant="body2">
                            <strong>Email:</strong> {userEmail}
                        </Typography>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose} disabled={isSubmitting}>
                    {hasSubmittedBatch ? "Close" : "Cancel"}
                </Button>

                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={submitDisabled}
                    startIcon={
                        isSubmitting ? (
                            <CircularProgress size={18} color="inherit" />
                        ) : null
                    }
                >
                    {isSubmitting
                        ? "Submitting..."
                        : hasSubmittedBatch
                            ? "Batch Submitted"
                            : isBatchInitiation
                                ? "Submit Batch Initiation"
                                : isBatchPdfUpload
                                    ? dryRun
                                        ? "Start PDF Dry Run"
                                        : "Process PDFs from MinIO"
                                    : dryRun
                                        ? "Start Facility / General Dry Run"
                                        : "Start Facility / General Upload"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default BatchUploadModal;
